import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";

/**
 * Cartão de capa: foto de largura cheia em cima, nome e apoio embaixo.
 *
 * Referência: o destaque "Menu for dinner / Chicken Baked / 30 min · Easy"
 * do Dribbble 15125197 e as capas do 27589455. É o cartão de um produto na
 * estante e o destaque do dia na home. A foto fica inteira à vista: nada
 * escrito por cima dela.
 *
 * No tablet e no computador a foto vai para a esquerda e o texto para o lado
 * (.capa-lado em sistema.css). `sobretitulo` é a etiqueta curta em ouro acima
 * do nome ("Today's pick").
 * `pilulas` são valores curtos ("30 min", "Easy") sob o nome.
 */
export function CartaoCapa({
  nome,
  linha2,
  sobretitulo,
  pilulas,
  foto,
  capa,
  proporcao = "16 / 10",
  linhasDoNome = 2,
  onClick,
}: {
  nome: string;
  linha2?: string;
  sobretitulo?: string;
  pilulas?: string[];
  foto?: string;
  /** Nó próprio para a imagem (CachedImage, por exemplo). */
  capa?: ReactNode;
  /** Proporção CSS da foto. Estante (capas largas) usa 16/10; destaque de receita (foto quadrada) usa 4/3. */
  proporcao?: string;
  /** Quantas linhas o nome pode ocupar antes de cortar. Numa grade de duas colunas (computador) o texto fica estreito: 3. */
  linhasDoNome?: 2 | 3;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="toque capa-lado block w-full overflow-hidden bg-card text-left"
      style={{ borderRadius: "var(--raio-cartao)", boxShadow: "inset 0 0 0 1px hsl(var(--border))", ["--capa-proporcao" as string]: proporcao } as React.CSSProperties}
    >
      <span className="capa-foto relative block w-full bg-muted">
        <span className="absolute inset-0">
          {capa ?? (foto ? <img src={foto} alt="" loading="lazy" className="h-full w-full object-cover" /> : null)}
        </span>
      </span>

      <span className="capa-texto flex items-center justify-between gap-3" style={{ padding: "14px 16px 16px" }}>
        <span className="min-w-0 flex-1">
          {sobretitulo && (
            <span
              className="block font-bold uppercase leading-none"
              style={{ fontSize: "var(--t-etiqueta)", letterSpacing: "0.12em", color: "hsl(var(--accent))", paddingBottom: 8 }}
            >
              {sobretitulo}
            </span>
          )}
          <span
            className="block font-bold leading-tight"
            style={{
              fontSize: "var(--t-item)",
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: linhasDoNome,
              overflow: "hidden",
            }}
          >
            {nome}
          </span>
          {linha2 && (
            <span className="block truncate pt-1 leading-snug" style={{ fontSize: "var(--t-corpo)", color: "var(--tinta-apoio)" }}>
              {linha2}
            </span>
          )}
          {pilulas && pilulas.length > 0 && (
            <span className="flex flex-wrap gap-2" style={{ paddingTop: 10 }}>
              {pilulas.map((p) => (
                <span
                  key={p}
                  className="inline-flex items-center rounded-full bg-secondary font-semibold leading-none"
                  style={{ fontSize: "var(--t-etiqueta)", padding: "8px 12px" }}
                >
                  {p}
                </span>
              ))}
            </span>
          )}
        </span>
        <ChevronRight className="h-7 w-7 shrink-0" strokeWidth={2.5} style={{ color: "var(--tinta-fraca)" }} />
      </span>
    </button>
  );
}
